import React from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, Table, Trash2, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

const StatusBadge = ({ status, className = "" }) => {
  return (
    <div className={cn("relative group shrink-0", className)}>
      {status === 'ready' ? (
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 dark:bg-emerald-400/60" />
      ) : status === 'failed' ? (
        <div className="w-1.5 h-1.5 rounded-full bg-red-500/60 dark:bg-red-400/60" />
      ) : (
        <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60 dark:bg-amber-400/60 animate-pulse" />
      )}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-10 shadow-lg">
        {status === 'ready' ? 'Ready' : status === 'failed' ? 'Failed' : status === 'loading' ? 'Loading' : status === 'generating' ? 'Generating' : status || 'Processing'}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100" />
      </div>
    </div>
  );
};

const GroupRow = ({ label, count, isExpanded, onToggle, icon: Icon, iconColor = "text-blue-500", level = 0 }) => {
  const bgClass = level === 0
    ? "bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/30 dark:hover:bg-gray-900/30"
    : "bg-gray-50/10 dark:bg-gray-950/10 hover:bg-gray-100/20 dark:hover:bg-gray-900/20";

  return (
    <tr
      className={cn(bgClass, "cursor-pointer border-b border-gray-100/50 dark:border-gray-800/50")}
      onClick={onToggle}
    >
      <td className={cn("px-6 py-2.5", level > 0 && "py-2")} colSpan={3}>
        <div className="flex items-center gap-2" style={{ paddingLeft: level === 0 ? '0' : '2rem' }}>
          {isExpanded ? (
            <ChevronDown size={level === 0 ? 12 : 11} className="text-gray-400" />
          ) : (
            <ChevronRight size={level === 0 ? 12 : 11} className="text-gray-400" />
          )}
          {isExpanded ? (
            <FolderOpen size={level === 0 ? 13 : 12} className={iconColor} />
          ) : (
            <Folder size={level === 0 ? 13 : 12} className={iconColor} />
          )}
          <span className={cn(
            "font-black uppercase tracking-wider",
            level === 0 ? "text-[10px] text-gray-700 dark:text-gray-300" : "text-[9px] text-gray-600 dark:text-gray-400"
          )}>
            {label}
          </span>
          <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
            ({count})
          </span>
        </div>
      </td>
    </tr>
  );
};

const ItemRow = ({
  item,
  onSelect,
  onRemove,
  onRetry,
  formatDate,
  itemIcon: ItemIcon = Table,
  level = 0,
  showStatus = false,
  status = null,
  getDisplayName = (item) => item.displayName || item.name
}) => {
  // Level 0: pl-6 (top level), Level 1: pl-14 (items in local groups), Level 2: pl-24 (items in datasets)
  const isReady = !status || status === 'ready';

  return (
    <tr
      onClick={(e) => {
        e.stopPropagation();
        if (isReady && onSelect) {
          onSelect(item);
        }
      }}
      className={cn(
        "group transition-all border-b border-gray-50/50 dark:border-gray-800/30",
        isReady ? "hover:bg-emerald-50/20 dark:hover:bg-emerald-900/5 cursor-pointer" : "opacity-80 cursor-not-allowed"
      )}
    >
      <td className="px-6 py-2.5">
        <div className="flex items-center gap-2" style={{ paddingLeft: level === 0 ? '0' : level === 1 ? '2rem' : '4.5rem' }}>
          <ItemIcon size={11} className="text-gray-400" />
          {showStatus && status && <StatusBadge status={status} />}
          <div className="flex-1 min-w-0">
            <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider block truncate max-w-[300px]">
              {item.name}
            </span>
            <span className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block max-w-[300px] mt-0.5 opacity-60">
              {getDisplayName(item)}
            </span>
          </div>
        </div>
      </td>
      <td className="px-6 py-2.5">
        <span className="text-[9px] font-mono text-gray-500 dark:text-gray-400">
          {formatDate(item.created_at)}
        </span>
      </td>
      <td className="px-6 py-2.5 text-right">
        <div className="flex items-center justify-end gap-2 pr-2">
          {showStatus && status && status !== 'ready' && onRetry && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRetry(item.id);
              }}
              className="p-1.5 text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors bg-gray-50 dark:bg-gray-800/50 rounded-lg"
              title="Retry"
            >
              <RefreshCw size={12} strokeWidth={2.5} className={cn(status !== 'failed' && "animate-spin")} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isReady && onSelect) {
                onSelect(item);
              }
            }}
            disabled={!isReady}
            className={cn(
              "px-3 py-1.5 text-[8px] font-black uppercase tracking-widest rounded-lg transition-all shadow-sm",
              isReady
                ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200/50 dark:shadow-none"
                : "bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed"
            )}
          >
            View
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove && onRemove(item.id);
            }}
            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
          >
            <Trash2 size={13} strokeWidth={2.5} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const TreeTable = ({
  // Data
  bigQueryTree = {},
  localGroups = {},
  customGroup = null,

  // State
  expandedProjects = new Set(),
  expandedDatasets = new Set(),
  expandedLocalGroups = new Set(),

  // Handlers
  onToggleProject,
  onToggleDataset,
  onToggleLocalGroup,
  onSelectItem,
  onRemoveItem,
  onRetryItem,

  // Configuration
  formatDate,
  itemIcon = Table,
  projectIconColor = "text-blue-500",
  datasetIconColor = "text-blue-400",
  localGroupIconColor = "text-emerald-500",
  showStatus = false,
  getItemStatus = (item) => item.status,
  getDisplayName = (item) => item.displayName || item.name,
  getItemName = (item) => item.name || item.functionName
}) => {
  const renderBigQueryTree = () => {
    return Object.entries(bigQueryTree).map(([project, datasets]) => {
      const isProjectExpanded = expandedProjects.has(project);
      const projectCount = Object.values(datasets).reduce((sum, items) => sum + items.length, 0);

      return (
        <React.Fragment key={project}>
          <GroupRow
            label={project}
            count={projectCount}
            isExpanded={isProjectExpanded}
            onToggle={() => onToggleProject(project)}
            icon={Folder}
            iconColor={projectIconColor}
            level={0}
          />

          {isProjectExpanded && Object.entries(datasets).map(([dataset, items]) => {
            const datasetKey = `${project}.${dataset}`;
            const isDatasetExpanded = expandedDatasets.has(datasetKey);

            return (
              <React.Fragment key={datasetKey}>
                <GroupRow
                  label={dataset}
                  count={items.length}
                  isExpanded={isDatasetExpanded}
                  onToggle={(e) => {
                    e.stopPropagation();
                    onToggleDataset(project, dataset);
                  }}
                  icon={Folder}
                  iconColor={datasetIconColor}
                  level={1}
                />

                {isDatasetExpanded && items.map(item => (
                  <ItemRow
                    key={item.id}
                    item={{ ...item, name: getItemName(item) }}
                    onSelect={onSelectItem}
                    onRemove={onRemoveItem}
                    onRetry={onRetryItem}
                    formatDate={formatDate}
                    itemIcon={itemIcon}
                    level={2}
                    showStatus={showStatus}
                    status={getItemStatus(item)}
                    getDisplayName={getDisplayName}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      );
    });
  };

  const renderLocalGroups = () => {
    return Object.entries(localGroups).map(([type, items]) => {
      const isGroupExpanded = expandedLocalGroups.has(type);
      const typeLabel = type === 'upload' ? 'Uploaded Files' :
        type === 'parquet' ? 'Parquet Files' :
          type === 'csv' ? 'CSV Files' : 'Other';

      return (
        <React.Fragment key={type}>
          <GroupRow
            label={typeLabel}
            count={items.length}
            isExpanded={isGroupExpanded}
            onToggle={() => onToggleLocalGroup(type)}
            icon={Folder}
            iconColor={localGroupIconColor}
            level={0}
          />

          {isGroupExpanded && items.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              onSelect={onSelectItem}
              onRemove={onRemoveItem}
              onRetry={onRetryItem}
              formatDate={formatDate}
              itemIcon={itemIcon}
              level={1}
              showStatus={showStatus}
              status={getItemStatus(item)}
              getDisplayName={getDisplayName}
            />
          ))}
        </React.Fragment>
      );
    });
  };

  const renderCustomGroup = () => {
    if (!customGroup || customGroup.length === 0) return null;

    return (
      <React.Fragment>
        <tr
          className="bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/30 dark:hover:bg-gray-900/30 cursor-pointer border-b border-gray-100/50 dark:border-gray-800/50"
        >
          <td className="px-6 py-2.5" colSpan={3}>
            <div className="flex items-center gap-2">
              <Folder size={13} className={localGroupIconColor} />
              <span className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Custom Functions
              </span>
              <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                ({customGroup.length})
              </span>
            </div>
          </td>
        </tr>
        {customGroup.map(item => (
          <ItemRow
            key={item.id}
            item={item}
            onSelect={onSelectItem}
            onRemove={onRemoveItem}
            formatDate={formatDate}
            itemIcon={itemIcon}
            level={1}
            showStatus={false}
            getDisplayName={(item) => item.signature || item.name}
          />
        ))}
      </React.Fragment>
    );
  };

  return (
    <>
      {renderLocalGroups()}
      {renderCustomGroup()}
      {renderBigQueryTree()}
    </>
  );
};

export default TreeTable;
